import { TeamsUserCredential } from '@microsoft/teamsfx';
import { ConfigTable, IDiscoResult, IInstance, ILaunchUrl } from '../interfaces';

export interface IPPPProject {
	id: string;
	name: string;
}

export interface IPPPApp {
	appmoduleid: string;
	solutionid: string;
	name: string;
	uniquename: string;
	ismanaged: boolean;
	description: string;
}

// Function to extract the url from the end of the hash of he tab
// e.g. ${{TAB_ENDPOINT}}/index.html#/launch?url=${{DATAVERSE_ENV_URL}}&pagetype=entitylist&etn=tpg_actionitem
// ?url param will be extracted and decoded if required
export function getLaunchUrls(themeString: string): ILaunchUrl {
	const isDarkMode = themeString === 'dark';

	// Get launch URL from param
	const launchUrl = getLaunchUrlParts();

	return getAppAndWebUrls(launchUrl, isDarkMode);
}

export function isConfigTab(): boolean {
	const currentUrl = new URL(window.location.href);

	// Check if we're in a config tab
	return currentUrl.href.includes('#/config');
}

export function getLaunchUrlParts(): URL {
	// This will not give us anymore the search params since they are after hash..
	const currentUrl = new URL(window.location.href);

	// ..so we extract the url from after the hash and ?url param
	let [, url] = currentUrl.href.split('#')[1].split('?url=');

	// If we're not in a personal tab return this Url
	if (!url) {
		return currentUrl;
	}

	// Backwards compat v1 the url was encoded
	if (url.startsWith('https%3A%2F%2F')) {
		url = decodeURIComponent(url);
	}

	// Get launch URL from param
	return new URL(url);
}

export function getAppAndWebUrls(launchUrl: URL, isDarkMode: boolean): ILaunchUrl {
	const launchUrls: ILaunchUrl = {
		appUrl: null,
		webUrl: null,
		host: null,
		authenticated: false,
		isDynamics: false
	};

	// Set host - NOTE: not being used anywhere so far
	launchUrls.host = launchUrl.origin;

	// Assure "webUrl" has NOT param navbar=off and "appUrl" has param navbar=off and
	if (launchUrl.searchParams.has('navbar')) {
		launchUrls.appUrl = launchUrl.href;
		launchUrl.searchParams.delete('navbar');
		launchUrls.webUrl = launchUrl.href;
	} else {
		launchUrls.webUrl = launchUrl.href;
		launchUrl.searchParams.append('navbar', 'off');
		launchUrls.appUrl = launchUrl.href;
	}

	// Append dark mode theme option
	if (isDarkMode) {
		launchUrls.appUrl = `${launchUrls.appUrl}&flags=themeOption%3Dteamsdark`;
	}

	// Confirm if Dynamics url
	if (launchUrl.host.includes('dynamics.com')){
		launchUrls.isDynamics = true;
	}

	return launchUrls;
}

// Get environments for the current user
export const discoverEnvironments = async (teamsUserCredential: TeamsUserCredential): Promise<IInstance[]> => {
	try {
		const accessToken = await teamsUserCredential.getToken([
			'https://globaldisco.crm.dynamics.com/user_impersonation',
		]);

		console.debug('globaldisco accessToken', accessToken);

		const response = await fetch('https://globaldisco.crm.dynamics.com/api/discovery/v2.0/Instances', {
			headers: {
				Authorization: `Bearer ${accessToken!.token}`,
			},
		});

		if (!response.ok) {
			const error = response.body ? await response.json() : response.statusText;throw new Error(
				`Status: ${response.status} ${error?.error?.message ?? JSON.stringify(error)}`,
				{ cause: error }
			);
		}
		const discoResult: IDiscoResult = await response.json();

		console.debug('globaldisco Result', discoResult);
		return discoResult?.value;
	} catch (error: any) {
		console.error(error);
		throw new Error(error);
	}
};

// Get projects for the current user
export const getProjects = async (
	teamsUserCredential: TeamsUserCredential,
	environment: IInstance,
	table: ConfigTable
): Promise<IPPPProject[]> => {
	try {
		const accessToken = await teamsUserCredential.getToken([`${environment.Url}/user_impersonation`]);

		console.debug('environment accessToken', accessToken);

		const fields = {
			id: table === 'tpg_projects' ? 'tpg_projectid' : 'msdyn_projectid',
			name:table === 'tpg_projects' ? 'tpg_subject': 'msdyn_subject'
		};

		let url = `${environment.Url}/api/data/v9.2/${table}?$select=${fields.id},${fields.name}&$orderby=${fields.name}`;

		const response = await fetch(url, {
			headers: {
				Authorization: `Bearer ${accessToken!.token}`,
			},
		});

		if (!response.ok) {
			const error = response.body ? await response.json() : response.statusText;throw new Error(
				`Status: ${response.status} ${error?.error?.message ?? JSON.stringify(error)}`,
				{ cause: error }
			);
		}
		const result = await response.json();

		return result?.value?.map((p: any) => ({ id: p[fields.id], name: p[fields.name] }));
	} catch (error: any) {
		console.error(error);
		throw new Error(error);
	}
};

export const getTPGApps = async (
	teamsUserCredential: TeamsUserCredential,
	environment: IInstance
): Promise<IPPPApp[]> => {
	try {
		const accessToken = await teamsUserCredential.getToken([`${environment.Url}/user_impersonation`]);

		console.debug('environment accessToken', accessToken);

		const response = await fetch(
			`${environment.Url}/api/data/v9.2/appmodules?$filter=uniquename eq 'tpg_scheduler' or uniquename eq 'tpg_TPGPowerProjectApp'&$select=appmoduleid,solutionid,name,uniquename,ismanaged`,
			{
				headers: {
					Authorization: `Bearer ${accessToken!.token}`,
				},
			}
		);

		if (!response.ok) {
			const error = response.body ? await response.json() : response.statusText;
			throw new Error(`Status: ${response.status} ${error?.error?.message ?? JSON.stringify(error)}`, { cause: error });
		}
		const result = await response.json();

		return (result?.value as IPPPApp[]) ?? [];
	} catch (error: any) {
		console.error(error);
		throw new Error(error);
	}
};

export const findPPPProjectTable = (apps: IPPPApp[]): ConfigTable | undefined => {
	let app = apps.find((a) => a.uniquename === 'tpg_scheduler');
	if (app) {
		return 'tpg_projects';
	} else {
		app = apps.find((a) => a.uniquename === 'tpg_TPGPowerProjectApp');
		return 'msdyn_projects'
	}
};
